import { StripePrice } from "@switcherstudio/switcher-api-client";
import { PlanDetails, PlanDetailsProps } from "../../PlanDetails";
import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo } from "react";
import { useGenericMultiSelect } from "components/generic-multiselect/useGenericMultiSelect";
import { ComponentItem } from "components/generic-multiselect/types";
import { Spinner } from "components/spinners/Spinner";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { ListButton } from "components/buttons/ListButton";
import dayjs from "dayjs";
import {
    MaintenancePlan,
    useMaintenancePlan
} from "hooks/useMaintenancePlan";

const pausedSubscriptionsEnabled =
    import.meta.env.VITE_ENABLE_PAUSED_SUBSCRIPTIONS === "true";

const maintenancePlanEnabled =
    import.meta.env.VITE_ENABLE_MAINTENANCE_PLAN === "true";

/** Plan name without the billing period (e.g. "Studio Plus Yearly" -> "studio plus") */
const getPlanFamily = (name?: string): string =>
    (name ?? "")
        .replace(
            /\b(?:Switcher|Monthly|Quarterly|Semiannual|Yearly|Annual)\b/gi,
            ""
        )
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

export interface SelectPlanStepProps {
    prices: StripePrice[];
    loading: boolean;
    currentPlan: string;
    selectPlan: (plan: StripePrice) => void;
    onHandleSubmit: (func) => void;
    onCancel: () => void | Promise<void>;
    onPause: () => void;
    onMaintenance: (plan: MaintenancePlan) => void;
}

export const SelectPlanStep = ({
    prices,
    loading,
    currentPlan,
    selectPlan,
    onHandleSubmit,
    onCancel,
    onPause,
    onMaintenance
}: SelectPlanStepProps) => {
    const { t } = useTranslation();
    const { userInfo } = useSelector((s: RootState) => s.user);

    const { data: customer, loading: customerLoading } = useSwitcherClient(
        (client) => client.stripe_GetCustomer,
        { suppressErrorBoundary: true, requestImmediately: true }
    );

    const subCancelledOrNonexistent = useMemo(
        () =>
            customer?.StripeSubscriptions?.[0]?.CanceledAt ||
            customer?.StripeSubscriptions?.[0]?.CancelAtPeriodEnd ||
            customer?.StripeSubscriptions?.length === 0,
        [customer]
    );

    /** 1-month base price of each plan family (e.g. "studio core", "studio plus", "business") */
    const monthlyByFamily = useMemo(() => {
        const map = new Map<string, number>();
        prices?.forEach((price) => {
            const family = getPlanFamily(price?.Name);
            if (
                family &&
                price?.Amount &&
                price?.RecurringInterval === "month" &&
                (price?.RecurringIntervalCount ?? 1) === 1 &&
                !map.has(family)
            ) {
                map.set(family, price.Amount);
            }
        });
        return map;
    }, [prices]);

    /** Maths to calculate the savings of purchasing of non-monthly subs */
    const savings = useMemo(() => {
        /** Helper function to calculate savings percentage */
        const calculateSavings = (price: StripePrice) => {
            const {
                Name: name,
                Amount: amount,
                RecurringInterval: interval,
                RecurringIntervalCount: intervalCount = 1
            } = price;
            if (!name || !amount) return null;

            const baseMonthly = monthlyByFamily.get(getPlanFamily(name));
            const baseAnnualCost = baseMonthly ? baseMonthly * 12 : null;

            if (!baseAnnualCost) return null;

            let multiplier = 1;
            if (interval === "month" && intervalCount > 1) {
                multiplier = 12 / intervalCount;
                // Only allow valid interval counts (e.g., 1, 2, 3, 4, 6, 12 months)
                const allowedIntervalCounts = [1, 2, 3, 4, 6, 12];
                if (!allowedIntervalCounts.includes(intervalCount)) return null; // Skip invalid interval counts
            } else if (interval !== "year") {
                return null; // Skip non-yearly plans that aren't multi-month
            }

            // Calculate annualized cost of the plan
            const annualizedCost = amount * multiplier;

            // If the plan is the same or more expensive than the base, no savings
            if (annualizedCost >= baseAnnualCost) return null;

            // Calculate percentage savings
            const percent = (1 - annualizedCost / baseAnnualCost) * 100;

            return percent ? { name, percent } : null;
        };

        /** Map prices to result objects and filter out invalid entries */
        return (
            prices?.map(calculateSavings).filter((item) => item !== null) || []
        );
    }, [prices, monthlyByFamily]);

    const sortPrices = useCallback(
        (a: StripePrice, b: StripePrice) => {
            // Special case for Launch plans - always at the top
            if (a?.Name?.includes("Launch")) {
                return -1;
            }
            if (b?.Name?.includes("Launch")) {
                return 1;
            }

            // Special case for Pass plans - always at the bottom
            if (a?.Name?.includes("Pass")) return 1;
            if (b?.Name?.includes("Pass")) return -1;

            // First group by plan type (Studio vs Business)
            const aIsStudio = a?.Name?.includes("Studio");
            const bIsStudio = b?.Name?.includes("Studio");

            if (aIsStudio && !bIsStudio) return -1; // Studio comes before Business
            if (!aIsStudio && bIsStudio) return 1; // Business comes after Studio

            // Within the same plan type, keep each plan family together (cheapest family first, e.g. Studio Core before Studio Plus)
            const aFamily = getPlanFamily(a?.Name);
            const bFamily = getPlanFamily(b?.Name);
            if (aFamily !== bFamily) {
                const aBase = monthlyByFamily.get(aFamily) ?? Infinity;
                const bBase = monthlyByFamily.get(bFamily) ?? Infinity;
                if (aBase !== bBase) return aBase - bBase;
                return aFamily < bFamily ? -1 : 1;
            }

            // Within the same family, sort by interval (monthly first, then yearly)
            if (a?.RecurringInterval !== b?.RecurringInterval) {
                return a?.RecurringInterval === "month" ? -1 : 1;
            }

            // then by length of the billing period (monthly, quarterly, semiannual)
            return (
                (a?.RecurringIntervalCount ?? 1) -
                (b?.RecurringIntervalCount ?? 1)
            );
        },
        [monthlyByFamily]
    );

    const planDetailsComponentItems: ComponentItem<StripePrice>[] =
        useMemo(() => {
            if (!prices?.length) return [];

            const visiblePrices = subCancelledOrNonexistent
                ? prices
                : prices.filter((price) => !price?.Name?.includes("Pass"));

            return [...visiblePrices]
                .sort(sortPrices)
                .map((price: StripePrice, key) => {
                    return {
                        id: price?.Id,
                        component: (
                            <PlanDetails
                                item={price}
                                key={key}
                                percentSavings={savings.filter((s) =>
                                    price?.Name?.includes(s?.name)
                                )}
                                disabled={false}
                            />
                        ),
                        baseObject: price
                    };
                });
        }, [prices, savings, sortPrices, subCancelledOrNonexistent]);

    const memoizedPlan = useMemo(
        () => (currentPlan != "" ? [currentPlan] : []),
        [currentPlan]
    );

    const { GenericMultiSelectComponent, handleSubmit, selectedIdSet } =
        useGenericMultiSelect<StripePrice, PlanDetailsProps>({
            items: planDetailsComponentItems,
            onSubmit: ([id]) => {
                if (selectPlan) {
                    const selectedPlan = selectedIdSet.has(id);
                    if (selectedPlan) {
                        const selected = prices?.filter(
                            (p) => p?.Id === id
                        )?.[0];
                        selectPlan(selected);
                    }
                }
            },
            previouslySelectedIds: memoizedPlan,
            allowNoSelection: false,
            loading
        });

    /** handler to get function from here to parent */
    useEffect(() => {
        if (selectedIdSet.size > 0) {
            onHandleSubmit(() => {
                handleSubmit();
            });
        }
    }, [onHandleSubmit, currentPlan, prices, handleSubmit, selectedIdSet]);

    const isLoading: boolean = useMemo(
        () => loading || customerLoading,
        [loading, customerLoading]
    );

    const showPause = useMemo(() => {
        return (
            pausedSubscriptionsEnabled &&
            userInfo?.Plan?.Details?.RecurringInterval === "month" &&
            userInfo?.Plan?.Details?.RecurringIntervalCount === 1 &&
            userInfo?.Plan?.Details?.Status !== "trialing"
        );
    }, [
        userInfo?.Plan?.Details?.RecurringInterval,
        userInfo?.Plan?.Details?.RecurringIntervalCount,
        userInfo?.Plan?.Details?.Status
    ]);

    const pauseIsDisabled = useMemo(() => {
        return (
            showPause &&
            dayjs()
                .add(2, "hours")
                .isAfter(dayjs(userInfo?.Plan?.Details?.ActiveUntil))
        );
    }, [userInfo?.Plan?.Details?.ActiveUntil, showPause]);

    const userIsOnMaintenancePlan = !!useMaintenancePlan();

    /** Core and Plus users can switch to the matching Maintenance plan */
    const maintenanceTarget = useMemo<MaintenancePlan | undefined>(() => {
        if (!maintenancePlanEnabled || userIsOnMaintenancePlan) return;
        const family = getPlanFamily(
            prices?.find((price) => price?.Id === currentPlan)?.Name
        );
        if (family === "studio core") return "core";
        if (family === "studio plus") return "plus";
    }, [prices, currentPlan, userIsOnMaintenancePlan]);

    return (
        <>
            {isLoading ? (
                <div className={styles["spinner-container"]}>
                    <Spinner />
                </div>
            ) : (
                !!prices && (
                    <>
                        <div
                            className={`${styles["content"]} ${styles["disabled"]}`}
                        >
                            {GenericMultiSelectComponent}
                            {!subCancelledOrNonexistent && (
                                <>
                                    {maintenanceTarget ? (
                                        <>
                                            <hr />
                                            <ListButton
                                                onClick={() =>
                                                    onMaintenance(
                                                        maintenanceTarget
                                                    )
                                                }
                                            >
                                                {t(
                                                    `subscription:switch-to-maintenance-plan`
                                                )}
                                            </ListButton>
                                        </>
                                    ) : showPause ? (
                                        <>
                                            <hr />
                                            <ListButton
                                                onClick={onPause}
                                                disabled={pauseIsDisabled}
                                            >
                                                {t(`subscription:pause-plan`)}
                                            </ListButton>
                                            {pauseIsDisabled && (
                                                <div
                                                    className={`${styles["pause-disabled-message"]}`}
                                                >
                                                    {t(
                                                        `subscription:pause-disabled-message`
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <hr />
                                    )}
                                    <ListButton
                                        onClick={onCancel}
                                        variant="danger"
                                    >
                                        {t(`subscription:cancel-plan`)}
                                    </ListButton>
                                </>
                            )}
                        </div>
                    </>
                )
            )}
        </>
    );
};
